<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <!-- Toolbar -->
    <div class="px-4 py-2.5 border-b border-gray-100 flex items-center gap-3">
      <!-- Sub-view switch: routines and schemas are different things and get
           different views — never one mixed list. -->
      <div class="flex rounded border border-gray-200 overflow-hidden">
        <button
          v-for="v in VIEWS"
          :key="v.id"
          class="px-3 py-1 text-xs"
          :class="view === v.id ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
          @click="setView(v.id)"
        >
          {{ v.label }}
        </button>
      </div>
      <input
        v-if="view === 'routines' || view === 'schemas'"
        v-model.trim="search"
        type="text"
        :placeholder="view === 'routines' ? 'Search routines...' : 'Search schemas...'"
        class="border border-gray-200 rounded px-2 py-1 text-xs w-56 focus:outline-none focus:border-gray-400"
      />
      <button
        v-if="view !== 'agent'"
        class="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
        :disabled="loading"
        @click="loadAll"
      >
        Refresh
      </button>
      <div class="ml-auto flex items-center gap-2">
        <!-- Sub-tab-scoped actions: each creates the thing its tab lists. -->
        <button
          v-if="canManageIngestion && view === 'routines'"
          class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400"
          @click="startNewRoutine"
        >
          New routine
        </button>
        <button
          v-if="canManageIngestion && view === 'schemas'"
          class="text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600"
          @click="wizardOpen = true"
        >
          Discover file
        </button>
      </div>
    </div>

    <!-- INGESTION AGENT: its own page, not a modal. First in the chain so it
         renders regardless of the config load the other views depend on. The
         session survives switching sub-tabs (module-scope session map);
         changing the file remounts onto that file's own session. -->
    <div v-if="view === 'agent'" class="p-3 flex flex-col" style="height: 46rem">
      <p v-if="agentFile" class="text-[11px] text-gray-400 font-mono truncate mb-1.5 shrink-0">
        {{ agentFile }}
      </p>
      <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
        <AgentExplore
          :key="agentFile"
          embedded
          auto-start
          session-mode="ingestion"
          :s3-key="agentFile"
          :panels="['scratchpad', 'file', 'proposal']"
          :start-label="agentFile ? 'Work out this file\'s structure' : 'Ask about this tenant\'s ingestion'"
        />
      </div>
    </div>

    <div v-else-if="loading" class="p-6">
      <LoadingIndicator message="Loading ingestion config..." />
    </div>
    <div v-else-if="loadError" class="p-6 text-sm text-gray-600">
      Couldn't load ingestion config.
      <button class="text-primary-500 hover:underline ml-1" @click="loadAll">Retry</button>
    </div>

    <!-- HISTORY: full-width, no rail -->
    <div v-else-if="view === 'history'" class="p-4">
      <ParseHistoryTable key="all-history" :can-manage="canManageIngestion" />
    </div>

    <!-- ROUTINES: routine rail + detail -->
    <div v-else-if="view === 'routines'" class="flex" style="min-height: 32rem">
      <div class="w-72 shrink-0 border-r border-gray-100 py-2 overflow-y-auto" style="max-height: 44rem">
        <button
          v-for="routine in filteredRoutines"
          :key="routine.id"
          class="w-full text-left px-3 py-1.5"
          :class="railClass(selectedRoutineId === routine.id)"
          @click="selectedRoutineId = routine.id"
        >
          <span class="flex items-center gap-1.5 text-xs">
            <span class="h-1.5 w-1.5 rounded-full shrink-0" :class="statusDotClass(routine.last_run_status)"></span>
            <span class="truncate font-medium">{{ routine.name }}</span>
          </span>
          <span class="block text-[11px] text-gray-400 font-mono truncate pl-3">{{ routine.s3_source_prefix }}</span>
        </button>
        <p v-if="!filteredRoutines.length" class="px-3 py-2 text-xs text-gray-400">No routines.</p>
      </div>
      <div class="flex-1 min-w-0 p-4 overflow-y-auto" style="max-height: 44rem">
        <RoutineDetail
          v-if="selectedRoutine"
          :routine="selectedRoutine"
          :all-schemas="schemas"
          :can-manage="canManageIngestion"
          @changed="onRoutineChanged"
          @deleted="onRoutineDeleted"
          @toast="toast"
          @select-schema="openSchema($event)"
        />
        <div v-else class="h-full flex items-center justify-center text-sm text-gray-400 py-20">
          Select a routine.
        </div>
      </div>
    </div>

    <!-- SCHEMAS: schema rail + detail -->
    <div v-else class="flex" style="min-height: 32rem">
      <div class="w-72 shrink-0 border-r border-gray-100 py-2 overflow-y-auto" style="max-height: 44rem">
        <div class="flex items-center gap-1 px-3 pb-1.5">
          <span class="text-[10px] uppercase tracking-wide text-gray-400 mr-1">Sort</span>
          <button
            class="text-[11px] px-1.5 py-0.5 rounded"
            :class="schemaSort === 'name' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-100'"
            @click="schemaSort = 'name'"
          >
            Name
          </button>
          <button
            class="text-[11px] px-1.5 py-0.5 rounded"
            :class="schemaSort === 'created' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-100'"
            @click="schemaSort = 'created'"
          >
            Newest
          </button>
        </div>
        <button
          v-for="schema in filteredSchemas"
          :key="schema.id"
          class="w-full text-left px-3 py-1.5"
          :class="railClass(selectedSchemaId === schema.id)"
          @click="selectedSchemaId = schema.id"
        >
          <span class="flex items-center gap-1.5">
            <span
              class="text-xs font-mono truncate"
              :class="schema.dont_parse ? 'text-gray-400' : ''"
            >{{ schema.table_name }}</span>
            <span
              v-if="schema.dont_parse"
              class="shrink-0 text-[10px] leading-none px-1 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700"
              title="Don't parse is set — auto-parse skips this schema"
            >off</span>
          </span>
          <span class="flex items-baseline gap-1 text-[11px] text-gray-400">
            <span class="truncate">
              {{ sheetTypeLabel(schema.sheet_type) }}
              · {{ schema.column_count || (schema.columns || []).length }} cols
            </span>
            <span class="ml-auto shrink-0 tabular-nums">{{ shortDate(schema.created_at) }}</span>
          </span>
        </button>
        <p v-if="!filteredSchemas.length" class="px-3 py-2 text-xs text-gray-400">
          No schemas — use Discover to create one from a file.
        </p>
      </div>
      <div class="flex-1 min-w-0 p-4 overflow-y-auto" style="max-height: 44rem">
        <SchemaDetail
          v-if="selectedSchema"
          :schema="selectedSchema"
          :routines="routines"
          :can-manage="canManageIngestion"
          @changed="onSchemaChanged"
          @deleted="onSchemaDeleted"
          @toast="toast"
          @select-routine="openRoutine($event)"
        />
        <div v-else class="h-full flex items-center justify-center text-sm text-gray-400 py-20">
          Select a schema.
        </div>
      </div>
    </div>

    <DiscoverWizard
      v-if="wizardOpen"
      @close="wizardOpen = false"
      @schema-created="onSchemaCreated"
      @toast="toast"
      @explore-with-agent="openAgent"
    />

    <EtlToast ref="toast" />
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import EtlToast from '@/components/ETL_Utility/EtlToast.vue';
import { statusDotClass, sheetTypeLabel } from '@/utils/etlFormat';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import ParseHistoryTable from './ParseHistoryTable.vue';
import RoutineDetail from './RoutineDetail.vue';
import SchemaDetail from './SchemaDetail.vue';
import DiscoverWizard from './DiscoverWizard.vue';
import AgentExplore from '@/components/Beta/AgentExplore.vue';

/**
 * The Ingestion workspace — replaces the old Discovery Mode / Schema Registry /
 * Parse Routines tab triplet. Three sub-views (Routines, Schemas, History),
 * one per object kind; cross-links jump between them (a routine's schema chip
 * opens that schema in the Schemas view and vice versa), and everything shares
 * one component so state survives.
 */
export default {
  name: 'Overview_ETL_Ingestion',
  components: {
    LoadingIndicator, EtlToast, ParseHistoryTable, RoutineDetail, SchemaDetail, DiscoverWizard,
    AgentExplore,
  },
  data() {
    return {
      VIEWS: [
        { id: 'routines', label: 'Routines' },
        { id: 'schemas', label: 'Schemas' },
        { id: 'history', label: 'History' },
        { id: 'agent', label: 'Ingestion Agent' },
      ],
      view: 'routines',
      schemas: [],
      routines: [],
      loading: true,
      loadError: false,
      search: '',
      selectedRoutineId: null,
      selectedSchemaId: null,
      newRoutine: null,
      wizardOpen: false,
      agentFile: '',   // s3_key when opened on a file; '' = estate session
      schemaSort: 'name',   // 'name' | 'created'
    };
  },
  computed: {
    filteredRoutines() {
      const q = this.search.toLowerCase();
      const routines = q
        ? this.routines.filter((r) => r.name.toLowerCase().includes(q)
          || (r.s3_source_prefix || '').toLowerCase().includes(q))
        : this.routines;
      return [...routines].sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredSchemas() {
      const q = this.search.toLowerCase();
      const schemas = q
        ? this.schemas.filter((s) => s.table_name.toLowerCase().includes(q))
        : this.schemas;
      return [...schemas].sort((a, b) => (
        this.schemaSort === 'created'
          ? new Date(b.created_at || 0) - new Date(a.created_at || 0)
          : a.table_name.localeCompare(b.table_name)
      ));
    },
    selectedRoutine() {
      if (this.selectedRoutineId === 'new') return this.newRoutine;
      return this.routines.find((r) => r.id === this.selectedRoutineId) || null;
    },
    selectedSchema() {
      return this.schemas.find((s) => s.id === this.selectedSchemaId) || null;
    },
    canManageIngestion() {
      // Was superuser-only. Now anyone whose role holds module_data_ingestion
      // on this tenant — the same grant the backend checks — so a customer
      // admin can create schemas and routines for their own feeds.
      return canAccessFeature(FEATURES.MODULE_DATA_INGESTION);
    },
  },
  async mounted() {
    await this.loadAll();
    this.applyPendingFocus();
  },
  methods: {
    statusDotClass,
    sheetTypeLabel,
    shortDate(d) {
      return d ? new Date(d).toLocaleDateString() : '';
    },
    // Deep-link entry point. The Feeds tab stashes a focus target in
    // localStorage then switches here; on first mount (above) and on every
    // subsequent switch to this tab (parent calls this via $refs) we consume it.
    // Reads+clears so it fires once. Falls back gracefully if the id is gone.
    applyPendingFocus() {
      let pending;
      try {
        pending = JSON.parse(localStorage.getItem('etl_ingestion_focus') || 'null');
      } catch {
        pending = null;
      }
      if (!pending) return;
      localStorage.removeItem('etl_ingestion_focus');
      if (pending.schemaId && this.schemas.some((s) => s.id === pending.schemaId)) {
        this.openSchema(pending.schemaId);
      } else if (pending.routineId && this.routines.some((r) => r.id === pending.routineId)) {
        this.openRoutine(pending.routineId);
      }
    },
    railClass(active) {
      return active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50';
    },
    setView(view) {
      this.view = view;
      this.search = '';
    },
    openSchema(id) {
      this.selectedSchemaId = id;
      this.view = 'schemas';
    },
    openRoutine(id) {
      this.selectedRoutineId = id;
      this.view = 'routines';
    },
    toast({ message, kind }) {
      this.$refs.toast.push(message, kind);
    },
    async loadAll() {
      this.loading = true;
      this.loadError = false;
      try {
        await this.fetchLists();
        if (!this.selectedRoutineId && this.routines.length) {
          this.selectedRoutineId = [...this.routines]
            .sort((a, b) => a.name.localeCompare(b.name))[0].id;
        }
        if (!this.selectedSchemaId && this.schemas.length) {
          this.selectedSchemaId = [...this.schemas]
            .sort((a, b) => a.table_name.localeCompare(b.table_name))[0].id;
        }
      } catch {
        this.loadError = true;
      }
      this.loading = false;
    },
    async fetchLists() {
      const [schemasRes, routinesRes] = await Promise.all([
        axios.get('/api/etl_module/schemas/', { params: { is_deleted: false } }),
        axios.get('/api/etl_module/parse-routines/'),
      ]);
      this.schemas = schemasRes.data.results || schemasRes.data;
      this.routines = routinesRes.data.results || routinesRes.data;
    },
    // Silent list refresh — keeps the current selection and doesn't blank the UI.
    async refreshLists() {
      try {
        await this.fetchLists();
      } catch {
        this.toast({ message: 'Failed to refresh lists.', kind: 'error' });
      }
    },
    startNewRoutine() {
      this.newRoutine = { id: null, name: '', s3_source_prefix: '', recursive: false, schemas: [] };
      this.selectedRoutineId = 'new';
      this.view = 'routines';
    },
    async onRoutineChanged(routine) {
      await this.refreshLists();
      if (routine && this.selectedRoutineId === 'new') {
        this.selectedRoutineId = routine.id;
        this.newRoutine = null;
      }
    },
    onRoutineDeleted(id) {
      this.newRoutine = null;
      this.routines = this.routines.filter((r) => r.id !== id);
      this.selectedRoutineId = this.routines.length ? this.routines[0].id : null;
    },
    async onSchemaChanged() {
      await this.refreshLists();
    },
    onSchemaDeleted(id) {
      this.schemas = this.schemas.filter((s) => s.id !== id);
      this.selectedSchemaId = this.schemas.length ? this.schemas[0].id : null;
    },
    async onSchemaCreated(schema) {
      await this.refreshLists();
      if (schema && schema.id) {
        this.openSchema(schema.id);
      }
    },
    // Hand a file to the agent by switching to its page. Close the wizard
    // first: the agent supersedes the one-shot classification that just
    // failed, and leaving both open invites the user to keep saving the bad
    // one. s3Key '' opens an ESTATE session: no file, the agent boots with
    // the whole configuration and browses S3 itself (uploads happen inside
    // the session via the File widget). A key opens on that file.
    openAgent({ s3Key }) {
      this.wizardOpen = false;
      this.agentFile = s3Key || '';
      this.setView('agent');
    },
  },
};
</script>
